import { type DataItem, formatDataType } from '@pn/core/domain/data';
import { getExportableQuery } from '@pn/core/domain/query';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { handleError } from '@pn/core/errors/handleError';
import { dynamicDataProvider } from '@pn/core/services/data/dynamicDataProvider';
import { isEmpty, isNil } from 'lodash-es';
import {
  removePersistentStreamingNotification,
  showPersistentStreamingNotification,
} from './streamingNotification';

type ExportedData = {
  success: boolean;
  data: DataItem[];
  totalCount: number;
  error?: string;
};

export async function exportWorkspaceItem(params: {
  item: WorkspaceItem;
  fields?: string[];
  silent?: boolean;
  geometry?: GeoJSON.Geometry;
}): Promise<ExportedData> {
  const { item, fields = [], silent = false, geometry } = params;

  let notificationId: undefined | string;

  const exportQuery = getExportableQuery(item.query);

  if (!isEmpty(fields)) exportQuery.fields = fields;
  if (!isNil(geometry)) exportQuery.geometry = geometry;

  const mapping = await dynamicDataProvider(item.dataSource).getDataMapping(
    item
  );

  try {
    if (!silent) {
      notificationId = showPersistentStreamingNotification({
        action: 'Exporting',
        dataType: item.dataType,
      });
    }

    const exportedData = await dynamicDataProvider(
      item.dataSource
    ).streamDataByQuery({ query: exportQuery, mapping });

    return {
      success: true,
      ...exportedData,
    };
  } catch (error) {
    handleError({
      error,
      notify: silent
        ? undefined
        : `Failed to export ${formatDataType(item.dataType)} data`,
    });

    return {
      success: false,
      data: [],
      totalCount: 0,
      error: String(error),
    };
  } finally {
    removePersistentStreamingNotification(notificationId);
  }
}
